import type { Organization } from "../../domain/organizations/organization.types";
import type { Language, LocalizedText } from "../../domain/shared/localized-text.types";
import type { OrganizationPageData } from "./organizationPage.types";
import { ServiceCategory } from "../../domain/services/service.types";
import { shouldShowServices } from "./organizationPage.features";

const localize = (value: LocalizedText | undefined, language: Language): string | undefined =>
  value?.[language] ?? value?.es;

const serviceCategoryLabels: Record<ServiceCategory, LocalizedText> = {
  [ServiceCategory.FOOD]: { es: "Alimentación", en: "Food" },
  [ServiceCategory.CRAFS]: { es: "Artesanías", en: "Crafts" },
  [ServiceCategory.WORKSHOP]: { es: "Taller", en: "Workshop" },
  [ServiceCategory.EVENT]: { es: "Eventos", en: "Events" },
  [ServiceCategory.EXPERIENCE]: { es: "Experiencia", en: "Experience" },
  [ServiceCategory.OTHER]: { es: "Otro", en: "Other" },
};

export const mapOrganizationToPage = (
  organization: Organization,
  language: Language
): OrganizationPageData => {
  const servicesVisible = shouldShowServices(organization);
  const visibleServiceIds = new Set(
    servicesVisible
      ? organization.serviceCatalog?.services.map(({ id }) => id) ?? []
      : []
  );

  return {
  id: organization.id,
  name: localize(organization.identity.name, language) ?? "",
  shortDescription: localize(organization.identity.summary, language) ?? "",
  longDescription: localize(organization.identity.description, language) ?? "",
  logo: organization.identity.logo?.src ?? "",
  backgroundImage: organization.identity.heroImage?.src,
  products: (organization.productCatalog?.products ?? [])
    .filter(({ id }) => !visibleServiceIds.has(id))
    .map((product) => ({
    id: product.id,
    name: localize(product.name, language) ?? "",
    description: localize(product.description, language) ?? "",
    image:
      (product.images?.length ?? 0) === 1
        ? product.images![0].src
        : (product.images ?? []).map(({ src }) => src),
    price: localize(product.price?.label, language),
    presentations: product.variants?.length
      ? product.variants.map((variant) => ({
          weight: localize(variant.name, language),
          price: localize(variant.price?.label, language) ?? "",
        }))
      : undefined,
  })),
  servicesVisible,
  ...(servicesVisible && organization.serviceCatalog?.services.length
    ? {
        services: [...organization.serviceCatalog.services]
          .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
          .map((service) => ({
            id: service.id,
            slug: service.slug,
            name: localize(service.name, language) ?? "",
            description: localize(service.description, language),
            images: (service.images ?? []).map((image) => ({
              src: image.src,
              alt: localize(image.alt, language) ?? "",
            })),
            category: service.category,
            categoryLabel: localize(serviceCategoryLabels[service.category], language) ?? "",
            price: localize(service.price?.label, language),
            priceFrom: localize(service.priceFrom?.label, language),
            durationMinutes: service.durationMinutes,
            unit: localize(service.unit, language),
            availability: localize(service.availability, language),
            status: service.status,
            featured: service.featured ?? false,
            primaryAction: service.primaryAction
              ? {
                  method: service.primaryAction.method,
                  target: service.primaryAction.target,
                  intent: service.primaryAction.intent,
                  label: localize(service.primaryAction.label, language),
                  messageTemplate: localize(service.primaryAction.messageTemplate, language),
                }
              : undefined,
            reservation: service.reservation
              ? {
                  method: service.reservation.method,
                  target: service.reservation.target,
                  requiresDate: service.reservation.requiresDate,
                  requiresPartySize: service.reservation.requiresPartySize,
                }
              : undefined,
            gallery: (service.gallery ?? []).map((image) => ({
              src: image.src,
              alt: localize(image.alt, language) ?? "",
            })),
            order: service.order ?? 0,
            tags: (service.tags ?? []).map((tag) => localize(tag, language) ?? ""),
          })),
      }
    : {}),
  videos: organization.media?.videos?.map((video) => ({
    id: video.src,
    url: video.src,
    thumbnail: video.thumbnail?.src,
    name: localize(video.title, language) ?? "",
    description: localize(video.description, language),
  })),
  images: (organization.media?.images ?? []).map((image, index) => ({
    id: `${organization.id}--image-${index + 1}`,
    url: image.src,
    name: localize(image.alt, language) ?? "",
  })),
  contactInfo: {
    email: organization.contact?.emails?.find((value) => value.trim()) ?? "",
    phone: organization.contact?.phones?.find((value) => value.trim()) ?? "",
    address:
      localize(organization.contact?.address, language) ??
      localize(organization.location?.address, language) ??
      "",
  },
  socialMedia: organization.contact?.social ?? {},
  location: organization.location?.coordinates
    ? {
        lat: organization.location.coordinates.latitude,
        lng: organization.location.coordinates.longitude,
      }
    : undefined,
  };
};
